import React, { ReactNode } from "react";

import {
  LookupIcon,
  MapAreaIcon,
  ProfilingIcon,
  StorageIcon
} from "@/assets/icons";
import { Divider } from "@/components/ui/Divider";
import TimeAgo from "@/components/ui/TimeAgo";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import { AnalysisConfigurationItem } from "@/types/project";
import { AreaInputType, EOutputAnalysisType } from "@/types/spatial";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

type ProjectItemProps = {
  data: AnalysisConfigurationItem;
  onOpenProject: (data: AnalysisConfigurationItem) => void;
  onDeleteProject: (data: AnalysisConfigurationItem) => void;
};

interface IconTextProps {
  icon: ReactNode;
  text: string;
}

const IconText: React.FC<IconTextProps> = ({ icon, text }) => {
  return (
    <div className="w-1/3 flex items-center gap-2">
      {icon}
      <span className="text-[10px]">{text}</span>
    </div>
  );
};

const ProjectItem = ({
  data,
  onOpenProject,
  onDeleteProject
}: ProjectItemProps) => {
  const { showConfirmation } = useConfirmationModal();

  const isOutputTypeProfiling =
    data.analysis_output_type === EOutputAnalysisType.PROFILING;

  const gridType = () => {
    if (!data.area_input_type) return "-";
    return data.area_input_type === AreaInputType.ADMIN_AREA
      ? "Adm Area"
      : "Catchment";
  };

  const onDeleteItem = (data: AnalysisConfigurationItem) => {
    showConfirmation({
      title: "Are you sure, you want to delete? ",
      message: "Once deleted, this action cannot be undone.",
      cancelLabel: "",
      confirmLabel: "Delete",
      onConfirm: () => onDeleteProject(data)
    });
  };

  const getOutputText = () => {
    if (!data.analysis_output_type) return "-";
    return isOutputTypeProfiling ? "Profiling" : "Grid";
  };

  return (
    <div
      className={twMerge(
        "w-full text-left rounded-lg h-auto btn-default group hover:bg-teal-50 hover:border-teal-200"
      )}
    >
      <div className="px-1 py-2 overflow-hidden hover:cursor-pointer">
        <div className="flex flex-col gap-1">
          <p className="text-[10px] text-txtPlaceholder">
            <TimeAgo date={data.updated_at} />
            {data.updated_by?.full_name && (
              <span> - by {data.updated_by.full_name}</span>
            )}
          </p>
          <p
            className={twMerge(
              "text-txtHeading font-bold line-clamp-2 group-hover:text-teal-500"
            )}
            title={data.name}
          >
            {data.name}
          </p>
          <p className="truncate text-[10px]" title={data.description}>
            {data.description}
          </p>
        </div>
        <Divider className="bg-white-smoke w-full h-[1px] my-2" />
        <div className="flex">
          <IconText
            icon={<StorageIcon width={16} className="fill-purple-500" />}
            text={`${data.dataset_count} datasets`}
          />
          <IconText icon={<MapAreaIcon width={16} />} text={gridType()} />
          <IconText
            icon={
              isOutputTypeProfiling ? (
                <ProfilingIcon width={14} className="fill-teal-500" />
              ) : (
                <LookupIcon width={14} className="fill-teal-500" />
              )
            }
            text={getOutputText()}
          />
        </div>
        <div className="hidden mt-3 w-full gap-2 group-hover:flex">
          <Button
            className="w-1/2 h-8 text-sm font-semibold btn-secondary"
            onClick={() => onDeleteItem(data)}
          >
            Delete
          </Button>
          <Button
            className="w-1/2 h-8 text-sm font-semibold btn-primary"
            onClick={() => onOpenProject(data)}
            data-testid={`project-item-${data.id}`}
          >
            Open
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectItem;
